import { FileHeader, HeaderMapping, PortfolioPropertiesMap, PropertyConfig } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, NonCancelableCustomEvent,Select, SelectProps } from '@amzn/awsui-components-react';
import React, { Component } from 'react';

import { constants } from '../../../../common/Localization';
import Table from '../common-components/NativeHtmlTable';
import { RESOURCE_TYPES_MAP, SHOW_MAPPED_OPTIONS } from '../common-util/resources';
import { OnChangeSync, SHOW_MAPPED_STATUS } from '../common-util/types';
import { WizardContentProps } from '../WizardContentProps';
import {
    EmptyState,
    FileHeaderColCell,
    MeasurementColCell,
    OptionalPropertySelectColCell,
    OverwriteColCell,
} from './HeaderMappingColumnDefinitions';

interface Props extends WizardContentProps {
    fileHeaders: FileHeader[];
    portfolioProperties: PortfolioPropertiesMap;
    optionalProperties: PropertyConfig[];
    onOptionalHeaderMappingChange: OnChangeSync;
    onUomChange: OnChangeSync;
    onOpenOverwriteModal: OnChangeSync;
    onSwitchOverwriteToggle: OnChangeSync;
}

interface State {
    items: HeaderMapping[];
    showMappedOption: SelectProps.Option;
    infoAlertVisible: boolean;
}

class OptionalPropertiesTable extends Component<Props, State> {
    columnDefinitions = [
        {
            cell: (item: HeaderMapping) => OptionalPropertySelectColCell(item,
                this.props.optionalProperties, this.props.onOptionalHeaderMappingChange),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.EXPECTED_SCHEMA,
            id: 'portfolioProperty',
        },
        {
            cell: (item: HeaderMapping) => FileHeaderColCell(item),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.FILE_HEADER,
            id: 'fileHeader',
        },
        {
            cell: (item: HeaderMapping) => MeasurementColCell(item, this.props.onUomChange),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.MEASUREMENT,
            id: 'UOM',
        },
        {
            cell: (item: HeaderMapping) => OverwriteColCell(item, this.props.onOpenOverwriteModal, this.props.onSwitchOverwriteToggle),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.OVERWRITE_RECORDS,
            id: 'overwrite',
        },
    ];

    showOverwriteCountColumn = false;
    showUOMColumn = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            items: [],
            showMappedOption: SHOW_MAPPED_OPTIONS[0],
            infoAlertVisible: true,
        };
    }

    componentDidMount() {
        this.generateTableItems();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevProps.currentStep !== this.props.currentStep || prevProps.optionalProperties !== this.props.optionalProperties
            || prevProps.fileHeaders !== this.props.fileHeaders || prevProps.portfolioProperties !== this.props.portfolioProperties
            || prevProps.propertiesConfig !== this.props.propertiesConfig || this.state.showMappedOption !== prevState.showMappedOption) {
            this.generateTableItems();
        }
    }

    render() {
        if (!RESOURCE_TYPES_MAP[this.props.wizardContext.type].hasOptionalHeaders) {
            return null;
        }
        const tableTitle = (
            <div className='awsui-util-action-stripe'>
                <div className='awsui-util-action-stripe-title'>
                    <h2>{constants.OPTIONAL_HEADERS_TITLE}
                        <span className='awsui-util-header-counter'> ({this.state.items.length})</span>
                    </h2>
                </div>
                <div className='awsui-util-action-stripe-group'>
                    <Select options={SHOW_MAPPED_OPTIONS} selectedOption={this.state.showMappedOption} onChange={this.onShowOptionChange}/>
                </div>
            </div>
        );
        const columnDefinitions = this.columnDefinitions.filter(columnDefinition =>
            !(columnDefinition.id === 'overwrite' && !this.showOverwriteCountColumn) &&
            !(columnDefinition.id === 'UOM' && !this.showUOMColumn),
        );
        const infoAlert = this.props.wizardContext.type === 'server' && this.state.infoAlertVisible ? (
            <Alert
                visible={this.state.infoAlertVisible}
                onDismiss={() => this.setState({infoAlertVisible: false})}
                dismissible 
                className='awsui-util-pb-m'
            >
                <ul>
                    <li>{constants.OPTIONAL_HEADERS_TOOLTIPS.OS}</li>
                    <li>{constants.OPTIONAL_HEADERS_TOOLTIPS.TARGET_HOST_TYPE}
                        <ol>
                            <li>{constants.OPTIONAL_HEADERS_TOOLTIPS.TARGET_HOST_TYPES.TYPE_1}</li>
                            <li>{constants.OPTIONAL_HEADERS_TOOLTIPS.TARGET_HOST_TYPES.TYPE_2}</li>
                        </ol>
                    </li>
                    <li>{constants.OPTIONAL_HEADERS_TOOLTIPS.UOM}</li>
                </ul>
            </Alert>
        ) : null;
        return (
            <div className='awsui-util-pt-l'>
                {infoAlert}
                <Table
                    columnDefinitions={columnDefinitions}
                    items={this.state.items}
                    tableTitle={tableTitle}
                    emptyState={EmptyState}
                    tableId='optionalHeaderMapping'
                />
            </div>
        );
    }

    onShowOptionChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        this.setState({
            showMappedOption: event.detail.selectedOption,
        });
    };

    generateTableItems(): void {
        const { portfolioProperties, fileHeaders, optionalProperties } = this.props;
        let overwriteCountSum = 0;
        const items = fileHeaders.filter((fileHeader: FileHeader) => {
            // skip 'Do not import' at index 0
            if (fileHeader.id === 0) {
                return false;
            }
            // skip file headers that are mapped to mandatory properties
            if (fileHeader.match && fileHeader.match !== 'ignore' && portfolioProperties[fileHeader.match]
                && !portfolioProperties[fileHeader.match].optional) {
                return false;
            }
            // skip unmapped headers when showing mapped headers only
            if (this.state.showMappedOption.value === SHOW_MAPPED_STATUS.MAPPED && (!fileHeader.match || fileHeader.match === 'ignore')) {
                return false;
            }
            // skip mapped headers when showing unmapped headers only
            if (this.state.showMappedOption.value === SHOW_MAPPED_STATUS.UNMAPPED && fileHeader.match && fileHeader.match !== 'ignore') {
                return false;
            }
            return true;
        }).map((fileHeader: FileHeader) => {
            const optionalProperty = optionalProperties.find((header: PropertyConfig) => fileHeader.match === header.value);
            const portfolioProperty = fileHeader.match ? portfolioProperties[fileHeader.match] : null;
            const overwriteCount = portfolioProperty?.overwriteCount || 0;
            overwriteCountSum += overwriteCount;
            return {
                UOM: optionalProperty?.UOM,
                dbUOM: optionalProperty?.dbUOM,
                fileHeader: fileHeader.label,
                fileHeaderIndex: fileHeader.id,
                fileIdToValuesMap: portfolioProperty?.fileIdToValuesMap || {},
                fileUOM: portfolioProperty?.fileUOM,
                idToValueMap: portfolioProperty?.idToValueMap,
                overwrite: portfolioProperty?.overwrite,
                overwriteCount,
                portfolioProperty: fileHeader.match || 'ignore',
                portfolioPropertyLabel: optionalProperty?.text || null,
                uniqueValues: fileHeader.uniqueValues,
            } as HeaderMapping;
        });
        this.showOverwriteCountColumn = overwriteCountSum !== 0;
        this.showUOMColumn = optionalProperties.some(propertyConfig => propertyConfig.UOM);
        this.setState({
            items,
        });
    }
}

export default OptionalPropertiesTable;
